import { useState } from "react"
import { ChevronDown, ChevronRight, Layers, Send } from "lucide-react"
import { useSubmitOrderRequestMutation } from "shared/redux/endpoints/procurementApprovals"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { ORDER_STATUS } from "../status"
import { getApiErrorMessage } from "../orderSubmitFeedback"
import { StatusBadge, Currency, Button } from "../ui"

// One group order row + its child orders, expand/collapse on click. `group`
// is a normalizeOrderOrGroupItem() row (type "group") — `orders` and `total`
// are already provided by the API (derived server-side from the children).
// Each child order row is itself clickable — group → list OR → OR detail.
// Shared between OrderRequestContainer (procurement staff) and
// MyOrdersContainer (requester's own orders) — same shape, only the
// `onSelectOrder` navigation target differs per caller.
//
// "Submit" bulk-submits every DRAFT order in the group (POST
// .../order-requests/{id}/submit — id-only, no body needed, so no separate
// fetch of each draft's full data before submitting). Non-draft members
// (already pending/approved/etc.) are left untouched.
const GroupOrderRow = ({ group, onSelectOrder }) => {
  const [open, setOpen] = useState(false)
  const [submitOrder, { isLoading: submitting }] = useSubmitOrderRequestMutation()
  const orders = group.orders || []
  const total = group.total
  const hasPurchase = group.purchases?.length > 0
  const draftOrders = orders.filter((o) => o.status === ORDER_STATUS.DRAFT)

  const handleSubmitDrafts = async (e) => {
    e.stopPropagation()
    const results = await Promise.allSettled(
      draftOrders.map((o) => submitOrder(o.id).unwrap())
    )
    const failed = results.filter((r) => r.status === "rejected")
    const succeeded = results.length - failed.length
    if (succeeded > 0) {
      showToastSuccess(
        succeeded === 1 ? "Order submitted for approval." : `${succeeded} orders submitted for approval.`
      )
    }
    if (failed.length > 0) {
      showToastError(
        getApiErrorMessage(
          failed[0].reason,
          `${failed.length} of ${results.length} order(s) couldn't be submitted. Please try again.`
        )
      )
    }
  }

  return (
    <div
      className={`pc-group-row${group.overBudget && !open ? " pc-row-over-budget" : hasPurchase ? " pc-row-has-po" : ""}`}
      style={{
        fontSize: 13.5,
        borderBottom: "1px solid var(--pc-border)",
        borderLeft: `4px solid ${open ? "var(--pc-brand)" : "transparent"}`,
        background: open ? "var(--pc-brand-soft)" : undefined,
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            setOpen((o) => !o)
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          cursor: "pointer",
        }}
      >
        {open ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
        <Layers size={14} aria-hidden style={{ color: "var(--pc-muted)" }} />
        <span style={{ fontWeight: 600 }}>{group.code}</span>
        <span style={{ color: "var(--pc-muted)" }}>{group.title}</span>
        {hasPurchase && (
          <span className="pc-muted" style={{ fontSize: 12 }}>
            · {group.purchases.length} PO
          </span>
        )}
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          {draftOrders.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Send aria-hidden />}
              disabled={submitting}
              onClick={handleSubmitDrafts}
            >
              {submitting ? "Submitting…" : `Submit (${draftOrders.length})`}
            </Button>
          )}
          <StatusBadge status={group.status} />
        </span>
        <span style={{ minWidth: 90, textAlign: "right" }}>
          <Currency value={total} />
        </span>
      </div>
      {open && (
        <div style={{ padding: "0 14px 14px 34px" }}>
          <div
            style={{
              borderLeft: "2px solid var(--pc-brand)",
              paddingLeft: 14,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--pc-brand)",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                marginBottom: 6,
              }}
            >
              {orders.length} order{orders.length === 1 ? "" : "s"} in {group.code}
            </div>
            <div
              style={{
                background: "var(--pc-surface, #fff)",
                border: "1px solid var(--pc-border)",
                borderRadius: "var(--pc-r-md, 8px)",
                overflow: "hidden",
              }}
            >
          {orders.length === 0 ? (
            <span className="pc-muted" style={{ display: "block", padding: 12 }}>No orders in this group.</span>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <th>OR Code</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th className="pc-th-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    className={o.overBudget ? "pc-row-over-budget" : o.purchases?.length > 0 ? "pc-row-has-po" : undefined}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onSelectOrder(o.id)
                    }}
                  >
                    <td>{o.code}</td>
                    <td>
                      {o.title}
                      {o.purchases?.length > 0 && (
                        <span className="pc-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                          · {o.purchases.length} PO
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="pc-td-right">
                      <Currency value={o.totalAmount} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupOrderRow
